import { useState } from 'react';
import type { KeyboardEvent, RefObject } from 'react';
import { CloseIcon } from '../assets/icons';

/**
 * A free-form tag input: type a value and press Enter (or comma) to add it. Added tags
 * render as removable chips **below** the input — no dropdown.
 */
export default function TagsInput({
  value,
  onChange,
  placeholder,
  invalid,
  inputRef,
  showHint = true,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  invalid?: boolean;
  /** Ref to the text field, so a parent "+ Add another…" link can focus it. */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Show the "press Enter, comma, or Add" helper line (default true). */
  showHint?: boolean;
}) {
  const [text, setText] = useState('');

  const add = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setText('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(text);
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-stretch gap-2">
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => add(text)}
          placeholder={placeholder}
          className={[
            'min-w-0 flex-1 rounded-lg border bg-white px-[15px] py-2.5 text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:outline-none focus:ring-2',
            invalid
              ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
              : 'border-[#e2e8f0] focus:border-brand focus:ring-brand/15',
          ].join(' ')}
        />
        <button
          type="button"
          // Prevent the input's onBlur from firing first and double-adding.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            add(text);
            inputRef?.current?.focus();
          }}
          disabled={!text.trim()}
          className="shrink-0 rounded-lg bg-brand px-4 text-sm font-medium text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-40"
        >
          Add
        </button>
      </div>
      {showHint && (
        <span className="text-[11px] text-[#94a3b8]">
          Type a value and press Enter, comma, or click <span className="font-medium">Add</span>.
        </span>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((tag, i) => (
            <span
              key={`${tag}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#eef4ff] py-1 pl-2.5 pr-1.5 text-xs font-medium text-brand"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                onClick={() => remove(i)}
                className="flex size-4 items-center justify-center rounded text-brand/70 transition-colors hover:bg-brand/10 hover:text-brand"
              >
                <CloseIcon className="size-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
